import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CalendarAppStateService } from '@modules/calendar-app/services/state.service';
import { type EventsFilters } from '@modules/calendar-app/types/events-filters';
import {
  ScrollToPosition,
  SELECTED_ROW_SCROLL_OPTIONS,
} from '@modules/common/services/scroll-to-index.injection-token';
import { isNil } from '@modules/common/utils/base';
import { SelectableListDirective } from '@modules/drag-n-drop/directives/selectable-list.directive';
import { SelectableItemDirective } from '@modules/drag-n-drop/directives/selectable.directive';
import { type SelectableListChain } from '@modules/drag-n-drop/types/selectable-list-chain';
import { EventComponent } from '@modules/elements/components/event/event.component';
import { ClickDirective } from '@modules/utils/directives/click/click.directive';
import type { KnotSource } from '@sharedlib/common/types/knowledge/knot-source';
import type { CalendarEventSummaryDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import { BehaviorSubject, merge, Subject } from 'rxjs';
import { filter, skip } from 'rxjs/operators';
import { StickyCalendarEventsStore } from './sticky-calendar-events.store';

@Component({
  standalone: true,
  selector: 'stch-sticky-events-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [EventComponent, ClickDirective, SelectableListDirective, SelectableItemDirective],
  templateUrl: './sticky-events-list.component.html',
  providers: [
    StickyCalendarEventsStore,
    { provide: ScrollToPosition, useFactory: (): BehaviorSubject<number> => new BehaviorSubject<number>(null) },
  ],
})
export class StickyEventsListComponent {
  public readonly filters = input.required<EventsFilters>();
  public readonly withTags = input(false);
  public readonly withKnots = input(false);
  public readonly knotsSource = input<KnotSource>();
  /** The chain this list opens, with the date-range list below it (see `SelectableListChain`). */
  public readonly selectionChain = input<SelectableListChain<CalendarEventSummaryDto> | null>(null);

  public readonly clickEvent = output<CalendarEventSummaryDto>();
  public readonly doubleClickEvent = output<CalendarEventSummaryDto>();

  private readonly calendarAppStateService = inject(CalendarAppStateService);
  private readonly store = inject(StickyCalendarEventsStore, { self: true });
  private readonly scrollToPosition = inject(ScrollToPosition, { self: true });
  private readonly rows = viewChildren(EventComponent, { read: ElementRef<HTMLElement> });

  protected readonly events = this.store.events;
  /** The rows in the shape the selectable list infers its row type from. */
  protected readonly listItems = computed(() => this.events().map((data) => ({ data })));
  protected readonly resetSelected = new Subject<void>();

  private readonly _effect1 = effect(() => {
    this.store.loadEvents({
      filters: this.filters(),
      withTags: this.withTags(),
      withKnots: this.withKnots(),
      withConnections: false,
      knotsSource: this.knotsSource(),
    });
  });

  constructor() {
    // A click on the folder already open resets this list too: the list below it clears its own selection as
    // its filters change, and an empty selection there does not clear this one. The count found on creation
    // is not a click.
    merge(
      this.calendarAppStateService.getRefreshAll(),
      this.calendarAppStateService.getVirtualFolderReselections().pipe(skip(1)),
    )
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.resetSelected.next();
      });

    // The list is its own scroll box, so a row the keyboard lands on is brought into view here.
    this.scrollToPosition
      .pipe(
        filter((index) => !isNil(index)),
        takeUntilDestroyed(),
      )
      .subscribe((index) => {
        this.rows()[index]?.nativeElement.scrollIntoView(SELECTED_ROW_SCROLL_OPTIONS);
      });
  }
}
